<template>
  <main class="preference-page">
    <div class="bg-gradient"></div>
    <div class="orb orb-1"></div>
    <div class="orb orb-2"></div>
    <div class="orb orb-3"></div>

    <div class="preference-brand-row">
      <desktop-brand-corner inline />
    </div>

    <header class="preference-header">
      <button type="button" class="back-btn" @click="handleGoBack" aria-label="返回上一页">
        <font-awesome-icon icon="chevron-left" />
      </button>
      <h1 class="preference-title">偏好设置</h1>
      <span class="header-spacer" aria-hidden="true"></span>
    </header>

    <section class="preference-shell" aria-label="调整顺序">
      <section class="sortable-section">
        <h3 class="sortable-heading">学习阶段顺序</h3>
        <div ref="stageOrderBoardRef" class="order-board glass-card">
          <TransitionGroup name="order-list" tag="div" class="order-list">
            <article
              v-for="(stage, index) in preferenceStages"
              :key="stage.id"
              class="order-row"
              :class="{ dragging: draggedItemId === stage.id }"
              :data-item-id="stage.id"
              draggable="true"
              :style="{
                '--stage-color': stage.color,
                '--stage-soft': stage.softColor,
                '--stage-shadow': stage.shadowColor
              }"
              @dragstart="handleDragStart(stage.id, 'stage')"
              @dragover.prevent
              @drop="handleDrop(stage.id, 'stage')"
              @dragend="handleDragEnd"
            >
              <div class="stage-card" @touchstart="handleTouchDragStart($event, stage.id, 'stage')">
                <span class="stage-icon">
                  <font-awesome-icon :icon="stage.icon" />
                </span>

                <span class="stage-copy">
                  <span class="stage-age">{{ stage.ageRange }}</span>
                  <strong class="stage-name">【{{ stage.title }}】</strong>
                  <span class="stage-desc">{{ stage.description }}</span>
                </span>

                <span class="stage-play">
                  <font-awesome-icon icon="grip-vertical" />
                </span>
              </div>

              <div class="rank-card">
                <span class="rank-number">{{ index + 1 }}</span>
                <span class="rank-actions" aria-label="调整顺序">
                  <button
                    type="button"
                    class="rank-action-btn"
                    :disabled="index === 0"
                    aria-label="上移"
                    @click="moveItem('stage', index, index - 1)"
                  >
                    <font-awesome-icon icon="chevron-up" />
                  </button>
                  <button
                    type="button"
                    class="rank-action-btn"
                    :disabled="index === preferenceStages.length - 1"
                    aria-label="下移"
                    @click="moveItem('stage', index, index + 1)"
                  >
                    <font-awesome-icon icon="chevron-down" />
                  </button>
                </span>
              </div>
            </article>
          </TransitionGroup>
        </div>
      </section>

      <section class="sortable-section">
        <h3 class="sortable-heading">打卡内容顺序</h3>
        <div ref="progressOrderBoardRef" class="order-board glass-card">
          <TransitionGroup name="order-list" tag="div" class="order-list">
            <article
              v-for="(item, index) in progressItems"
              :key="item.id"
              class="order-row"
              :class="{ dragging: draggedItemId === item.id }"
              :data-item-id="item.id"
              draggable="true"
              :style="{
                '--stage-color': item.color,
                '--stage-soft': item.softColor,
                '--stage-shadow': item.shadowColor
              }"
              @dragstart="handleDragStart(item.id, 'progress')"
              @dragover.prevent
              @drop="handleDrop(item.id, 'progress')"
              @dragend="handleDragEnd"
            >
              <div class="stage-card" @touchstart="handleTouchDragStart($event, item.id, 'progress')">
                <span class="stage-icon">
                  <font-awesome-icon :icon="item.icon" />
                </span>

                <span class="stage-copy">
                  <span class="stage-age">{{ item.label }}</span>
                  <strong class="stage-name">{{ item.title }}</strong>
                </span>

                <span class="stage-play">
                  <font-awesome-icon icon="grip-vertical" />
                </span>
              </div>

              <div class="rank-card">
                <span class="rank-number">{{ index + 1 }}</span>
                <span class="rank-actions" aria-label="调整顺序">
                  <button
                    type="button"
                    class="rank-action-btn"
                    :disabled="index === 0"
                    aria-label="上移"
                    @click="moveItem('progress', index, index - 1)"
                  >
                    <font-awesome-icon icon="chevron-up" />
                  </button>
                  <button
                    type="button"
                    class="rank-action-btn"
                    :disabled="index === progressItems.length - 1"
                    aria-label="下移"
                    @click="moveItem('progress', index, index + 1)"
                  >
                    <font-awesome-icon icon="chevron-down" />
                  </button>
                </span>
              </div>
            </article>
          </TransitionGroup>
        </div>
      </section>
    </section>
  </main>
</template>

<script setup>
import { onBeforeUnmount, ref } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()
const STAGE_ORDER_STORAGE_KEY = 'tree-sprout-stage-order'
const PROGRESS_ORDER_STORAGE_KEY = 'tree-sprout-progress-card-order'

const defaultPreferenceStages = [
  {
    id: 'sense',
    ageRange: '0-3岁',
    title: '启蒙感知',
    description: '通过趣味动画和儿歌培养英语兴趣',
    icon: 'palette',
    color: '#5b8ff9',
    softColor: '#eef4ff',
    shadowColor: 'rgba(91, 143, 249, 0.18)',
  },
  {
    id: 'sprout',
    ageRange: '3-6岁',
    title: '语言萌芽',
    description: '基础词汇和简单句型学习',
    icon: 'seedling',
    color: '#4caf50',
    softColor: '#e8f5e9',
    shadowColor: 'rgba(76, 175, 80, 0.18)',
  },
  {
    id: 'expression',
    ageRange: '6-9岁',
    title: '表达发展',
    description: '口语表达和简单对话练习',
    icon: 'comments',
    color: '#ffb020',
    softColor: '#fff3dc',
    shadowColor: 'rgba(255, 176, 32, 0.2)',
  },
  {
    id: 'ability',
    ageRange: '9-12岁',
    title: '能力提升',
    description: '阅读理解和简单书写',
    icon: 'book',
    color: '#9c6ade',
    softColor: '#f1e8ff',
    shadowColor: 'rgba(156, 106, 222, 0.18)',
  },
]

const defaultProgressItems = [
  {
    id: 'calendar',
    label: '打卡',
    title: '宝贝每日打卡历',
    icon: 'calendar-days',
    color: '#4caf50',
    softColor: '#e8f5e9',
    shadowColor: 'rgba(76, 175, 80, 0.18)',
  },
  {
    id: 'weekly-time',
    label: '数据',
    title: '本周学习时长',
    icon: 'chart-line',
    color: '#ff8a3d',
    softColor: '#fff3e8',
    shadowColor: 'rgba(255, 138, 61, 0.2)',
  },
  {
    id: 'vocab',
    label: '词汇',
    title: '累计已学习词汇量',
    icon: 'book-open',
    color: '#5b8ff9',
    softColor: '#eef4ff',
    shadowColor: 'rgba(91, 143, 249, 0.18)',
  },
]

const normalizeItemOrder = (items, savedOrder) => {
  const defaultIds = items.map((item) => item.id)
  const validSavedIds = Array.isArray(savedOrder)
    ? savedOrder.filter((id) => defaultIds.includes(id))
    : []

  return [...new Set([...validSavedIds, ...defaultIds])]
}

const getSavedItemOrder = (storageKey, items) => {
  try {
    return normalizeItemOrder(items, JSON.parse(window.localStorage.getItem(storageKey) || '[]'))
  } catch {
    return normalizeItemOrder(items, [])
  }
}

const buildItemsByOrder = (items, order) => {
  return order
    .map((id) => items.find((item) => item.id === id))
    .filter(Boolean)
}

const preferenceStages = ref(buildItemsByOrder(
  defaultPreferenceStages,
  getSavedItemOrder(STAGE_ORDER_STORAGE_KEY, defaultPreferenceStages)
))
const progressItems = ref(buildItemsByOrder(
  defaultProgressItems,
  getSavedItemOrder(PROGRESS_ORDER_STORAGE_KEY, defaultProgressItems)
))
const draggedItemId = ref('')
const draggedListType = ref('')
const stageOrderBoardRef = ref(null)
const progressOrderBoardRef = ref(null)
const LONG_PRESS_DELAY = 800
const DRAG_CANCEL_DISTANCE = 8
const touchDragState = {
  active: false,
  type: '',
  stageId: '',
  timerId: 0,
  startX: 0,
  startY: 0,
}

const getListByType = (type) => {
  return type === 'progress' ? progressItems : preferenceStages
}

const getStorageKeyByType = (type) => {
  return type === 'progress' ? PROGRESS_ORDER_STORAGE_KEY : STAGE_ORDER_STORAGE_KEY
}

const getBoardRefByType = (type) => {
  return type === 'progress' ? progressOrderBoardRef : stageOrderBoardRef
}

const saveItemOrder = (type) => {
  const list = getListByType(type)
  window.localStorage.setItem(getStorageKeyByType(type), JSON.stringify(list.value.map((item) => item.id)))
}

const moveItem = (type, fromIndex, toIndex) => {
  const list = getListByType(type)

  if (
    fromIndex === toIndex ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= list.value.length ||
    toIndex >= list.value.length
  ) {
    return
  }

  const nextItems = [...list.value]
  const [movedItem] = nextItems.splice(fromIndex, 1)
  nextItems.splice(toIndex, 0, movedItem)
  list.value = nextItems
  saveItemOrder(type)
}

const handleDragStart = (itemId, type) => {
  draggedItemId.value = itemId
  draggedListType.value = type
}

const handleDrop = (targetItemId, type) => {
  if (draggedListType.value !== type) {
    handleDragEnd()
    return
  }

  const list = getListByType(type)
  const fromIndex = list.value.findIndex((item) => item.id === draggedItemId.value)
  const toIndex = list.value.findIndex((item) => item.id === targetItemId)
  moveItem(type, fromIndex, toIndex)
  handleDragEnd()
}

const handleDragEnd = () => {
  draggedItemId.value = ''
  draggedListType.value = ''
}

const getItemIdByTouchY = (clientY, type) => {
  const boardRef = getBoardRefByType(type)
  const rows = Array.from(boardRef.value?.querySelectorAll('.order-row') || [])

  if (!rows.length) {
    return ''
  }

  const closestRow = rows.reduce((closest, row) => {
    const rect = row.getBoundingClientRect()
    const distance = Math.abs(clientY - (rect.top + rect.height / 2))

    if (!closest || distance < closest.distance) {
      return { row, distance }
    }

    return closest
  }, null)

  return closestRow?.row?.dataset?.itemId || ''
}

const clearTouchDragTimer = () => {
  if (touchDragState.timerId) {
    window.clearTimeout(touchDragState.timerId)
    touchDragState.timerId = 0
  }
}

const removeTouchDragListeners = () => {
  window.removeEventListener('touchmove', handleTouchDragMove)
  window.removeEventListener('touchend', handleTouchDragEnd)
  window.removeEventListener('touchcancel', handleTouchDragEnd)
}

const resetTouchDragState = () => {
  clearTouchDragTimer()
  touchDragState.active = false
  touchDragState.type = ''
  touchDragState.stageId = ''
  touchDragState.startX = 0
  touchDragState.startY = 0
  handleDragEnd()
  removeTouchDragListeners()
}

const handleTouchDragStart = (event, itemId, type) => {
  if (event.touches.length !== 1) {
    return
  }

  const touch = event.touches[0]
  clearTouchDragTimer()
  touchDragState.active = false
  touchDragState.type = type
  touchDragState.stageId = itemId
  touchDragState.startX = touch.clientX
  touchDragState.startY = touch.clientY

  touchDragState.timerId = window.setTimeout(() => {
    touchDragState.active = true
    draggedItemId.value = itemId
    draggedListType.value = type
  }, LONG_PRESS_DELAY)

  window.addEventListener('touchmove', handleTouchDragMove, { passive: false })
  window.addEventListener('touchend', handleTouchDragEnd)
  window.addEventListener('touchcancel', handleTouchDragEnd)
}

const handleTouchDragMove = (event) => {
  if (!touchDragState.stageId || event.touches.length !== 1) {
    resetTouchDragState()
    return
  }

  const touch = event.touches[0]
  const deltaX = touch.clientX - touchDragState.startX
  const deltaY = touch.clientY - touchDragState.startY
  const distance = Math.hypot(deltaX, deltaY)

  if (!touchDragState.active) {
    if (distance > DRAG_CANCEL_DISTANCE) {
      resetTouchDragState()
    }

    return
  }

  event.preventDefault()

  const list = getListByType(touchDragState.type)
  const targetItemId = getItemIdByTouchY(touch.clientY, touchDragState.type)
  const fromIndex = list.value.findIndex((item) => item.id === draggedItemId.value)
  const toIndex = list.value.findIndex((item) => item.id === targetItemId)
  moveItem(touchDragState.type, fromIndex, toIndex)
}

const handleTouchDragEnd = () => {
  resetTouchDragState()
}

const handleGoBack = () => {
  if (window.history.length > 1) {
    router.back()
    return
  }

  router.push('/home')
}

onBeforeUnmount(() => {
  resetTouchDragState()
})
</script>

<style scoped>
.preference-page {
  min-height: 100vh;
  position: relative;
  overflow-x: hidden;
  color: #2d3436;
  padding: 22px 28px 44px;
  font-family: 'Nunito', 'Source Han Sans CN', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
}

.bg-gradient {
  position: fixed;
  inset: 0;
  z-index: -3;
  background: linear-gradient(135deg, #faf8f5 0%, #f5f0eb 50%, #ede7e0 100%);
}

.orb {
  position: fixed;
  border-radius: 50%;
  filter: blur(100px);
  opacity: 0.5;
  z-index: -2;
  pointer-events: none;
}

.orb-1 {
  width: 500px;
  height: 500px;
  top: -150px;
  right: -100px;
  background: linear-gradient(135deg, #ffe8d6, #ffd4b8);
}

.orb-2 {
  width: 400px;
  height: 400px;
  left: -100px;
  bottom: -100px;
  background: linear-gradient(135deg, #e8f5e9, #c8e6c9);
}

.orb-3 {
  width: 350px;
  height: 350px;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: linear-gradient(135deg, #e3f2fd, #bbdefb);
  opacity: 0.3;
}

.glass-card {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.58);
  box-shadow:
    0 18px 48px rgba(45, 52, 54, 0.08),
    inset 0 1px 0 rgba(255, 255, 255, 0.84);
}

.preference-header {
  width: min(100%, 840px);
  min-height: 48px;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
}

.preference-brand-row {
  display: flex;
  justify-content: flex-start;
}

@media (max-width: 768px) {
  .preference-brand-row {
    display: none;
  }
}

@media (min-width: 769px) {
  .preference-brand-row {
    position: absolute;
    top: 8px;
    left: 24px;
    z-index: 2;
  }

  .preference-page {
    padding-top: 88px;
  }
}

.back-btn {
  position: absolute;
  left: 0;
  top: 50%;
  width: 44px;
  height: 44px;
  border: 1px solid rgba(255, 255, 255, 0.85);
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.82);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  color: #5f5044;
  font-size: 16px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
  transform: translateY(-50%);
  cursor: pointer;
  display: none;
  align-items: center;
  justify-content: center;
}

.back-btn:hover {
  color: #ff8a3d;
  box-shadow: 0 12px 28px rgba(255, 138, 61, 0.14);
}

.header-spacer {
  display: none;
}

.preference-title {
  margin: 0;
  font-size: 22px;
  line-height: 1.2;
  font-weight: 900;
  letter-spacing: 0;
}

.preference-shell {
  width: min(100%, 840px);
  margin: 28px auto 0;
  animation: fadeInUp 0.45s ease-out both;
}

.sortable-section + .sortable-section {
  margin-top: 28px;
}

.sortable-heading {
  margin: 0 0 12px;
  color: #5f5044;
  font-size: 17px;
  line-height: 1.25;
  font-weight: 900;
}

.order-board {
  position: relative;
  overflow: hidden;
  border-radius: 22px;
  padding: 26px 22px;
  user-select: none;
}

.order-board::before {
  content: '';
  position: absolute;
  left: -28px;
  top: 0;
  bottom: 0;
  width: 48%;
  background: linear-gradient(90deg, rgba(255, 232, 214, 0.72), rgba(255, 232, 214, 0.18));
  pointer-events: none;
}

.order-list {
  position: relative;
  z-index: 1;
}

.order-list-move,
.order-list-enter-active,
.order-list-leave-active {
  transition: transform 0.28s cubic-bezier(0.2, 0.8, 0.2, 1), opacity 0.2s ease;
}

.order-list-enter-from,
.order-list-leave-to {
  opacity: 0;
  transform: scale(0.96);
}

.order-row {
  position: relative;
  display: grid;
  grid-template-columns: minmax(0, 1fr) 176px;
  gap: 20px;
  align-items: stretch;
  cursor: grab;
  transition: opacity 0.2s ease, transform 0.28s cubic-bezier(0.2, 0.8, 0.2, 1);
}

.order-row.dragging {
  opacity: 0.56;
  transform: scale(0.99);
}

.order-row:active {
  cursor: grabbing;
}

.order-row + .order-row {
  margin-top: 16px;
}

.stage-card,
.rank-card {
  min-height: 86px;
  border-radius: 18px;
  border: 1px solid rgba(255, 255, 255, 0.7);
  background: rgba(255, 255, 255, 0.78);
  box-shadow: 0 10px 28px rgba(45, 52, 54, 0.06);
}

.stage-card {
  width: 100%;
  padding: 16px 20px;
  display: grid;
  grid-template-columns: 62px minmax(0, 1fr) 42px;
  gap: 16px;
  align-items: center;
  text-align: left;
  color: inherit;
  cursor: inherit;
  touch-action: pan-y;
  -webkit-touch-callout: none;
  transition: transform 0.22s ease, box-shadow 0.22s ease, background 0.22s ease;
}

.stage-card:hover {
  transform: translateY(-2px);
  background: rgba(255, 255, 255, 0.9);
  box-shadow: 0 16px 34px var(--stage-shadow);
}

.stage-icon {
  width: 58px;
  height: 58px;
  border-radius: 16px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--stage-color);
  font-size: 24px;
  background: var(--stage-soft);
}

.stage-copy {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 5px;
}

.stage-age {
  color: var(--stage-color);
  font-size: 13px;
  line-height: 1;
  font-weight: 900;
}

.stage-name {
  color: #2d3436;
  font-size: 17px;
  line-height: 1.25;
  font-weight: 900;
}

.stage-desc {
  color: #707782;
  font-size: 13px;
  line-height: 1.35;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.stage-play {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: var(--stage-color);
  font-size: 14px;
  background: #fff;
  box-shadow: 0 10px 26px var(--stage-shadow);
}

.rank-card {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
}

.rank-number {
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #2d3436;
  font-size: 25px;
  font-weight: 900;
  background: rgba(255, 255, 255, 0.86);
  border: 3px solid rgba(76, 175, 80, 0.24);
  box-shadow: 0 10px 24px rgba(76, 175, 80, 0.12);
}

.rank-actions {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.rank-action-btn {
  width: 34px;
  height: 34px;
  border: none;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 243, 224, 0.92);
  color: #ff8a3d;
  font-size: 13px;
  cursor: pointer;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.72);
}

.rank-action-btn:disabled {
  opacity: 0.34;
  cursor: not-allowed;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(24px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@media (max-width: 640px) {
  .preference-page {
    padding: calc(env(safe-area-inset-top, 0px) + 16px) 16px 28px;
  }

  .back-btn {
    display: inline-flex;
  }

  .preference-title {
    font-size: 20px;
  }

  .preference-shell {
    margin-top: 22px;
  }

  .order-board {
    padding: 16px 12px;
    border-radius: 20px;
  }

  .order-row {
    grid-template-columns: minmax(0, 1fr) 76px;
    gap: 10px;
  }

  .stage-card {
    min-height: 72px;
    grid-template-columns: 44px minmax(0, 1fr) 32px;
    gap: 10px;
    padding: 10px 11px;
    border-radius: 16px;
  }

  .stage-icon {
    width: 42px;
    height: 42px;
    border-radius: 14px;
    font-size: 18px;
  }

  .stage-play {
    width: 30px;
    height: 30px;
    font-size: 11px;
  }

  .stage-name {
    font-size: 14px;
  }

  .stage-desc {
    font-size: 11px;
  }

  .rank-card {
    min-height: 72px;
    border-radius: 16px;
    gap: 7px;
  }

  .rank-number {
    width: 38px;
    height: 38px;
    font-size: 20px;
  }

  .rank-action-btn {
    width: 26px;
    height: 26px;
    border-radius: 10px;
  }
}
</style>
